import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CardNB, ButtonNB, IconNB } from '../../../components/ui-nb';
import { useAuthStore } from '../../../stores/authStore';
import { useSparePartsList } from '../../../hooks/useSparePartsList';
import SparePartStatusBadge from '../../../components/spare-parts/SparePartStatusBadge';
import CreateSparePartModal from '../../../components/spare-parts/CreateSparePartModal';
import type { Tables } from '@tellowup/db';

// Intersection type for spare_parts with its joined order
type SparePartWithOrder = Tables<'spare_parts'> & {
  orders?: Tables<'orders'> | null;
};

const STATUS_OPTIONS = ['sin_pedir', 'pedido', 'recibido'];

export default function SparePartsList() {
  const { lang } = useParams();
  const navigate = useNavigate();
  const organizationId = useAuthStore(s => s.user?.organizationId) ?? null;

  // Filters
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { spareParts, totalCount, isLoading, error, refetch } = useSparePartsList({
    organizationId,
    status: statusFilter,
    page: currentPage,
  });

  const PAGE_SIZE = 20;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-nb-black">Refacciones</h1>
        <ButtonNB variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
          <IconNB name="plus" size={16} />
          New Spare Part
        </ButtonNB>
      </div>

      {/* Status Filter */}
      <CardNB className="p-4 mb-6">
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="font-nb font-medium text-sm text-nb-text-secondary block mb-1">
              Status
            </label>
            <select
              value={statusFilter || ''}
              onChange={(e) => {
                setStatusFilter(e.target.value || null);
                setCurrentPage(0);
              }}
              className="bg-nb-white border-3 border-nb-black px-4 py-3 text-sm font-nb rounded-[2px] focus:border-nb-yellow focus:outline-none"
            >
              <option value="">All</option>
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <ButtonNB
            variant="secondary"
            size="sm"
            disabled={!statusFilter}
            onClick={() => {
              setStatusFilter(null);
              setCurrentPage(0);
            }}
          >
            Clear
          </ButtonNB>
        </div>
      </CardNB>

      {/* Error State */}
      {error && (
        <div className="mb-4 px-4 py-3 rounded bg-nb-red/10 text-nb-red text-sm">
          {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <p className="text-nb-text-muted">Loading...</p>
      ) : spareParts.length === 0 ? (
        <CardNB className="text-center p-8">
          <p className="font-nb font-bold text-nb-text-muted mb-4">
            No spare parts registered
          </p>
          <ButtonNB variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
            <IconNB name="plus" size={16} />
            New Spare Part
          </ButtonNB>
        </CardNB>
      ) : (
        <>
          {/* Spare Parts Table */}
          <CardNB className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-3 border-nb-black bg-nb-cream">
                    <th className="text-left p-4 font-nb font-bold text-nb-text-muted">Description</th>
                    <th className="text-left p-4 font-nb font-bold text-nb-text-muted">Status</th>
                    <th className="text-left p-4 font-nb font-bold text-nb-text-muted">Order</th>
                    <th className="text-left p-4 font-nb font-bold text-nb-text-muted">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {spareParts.map((part, index) => (
                    <tr
                      key={part.id}
                      className={`border-b border-nb-black/10 cursor-pointer hover:bg-nb-yellow/30 ${
                        index % 2 === 0 ? 'bg-white' : 'bg-nb-cream/50'
                      }`}
                      onClick={() => navigate(`/${lang}/admin/spare-parts/${part.id}`)}
                    >
                      <td className="p-4 font-nb font-medium text-nb-black">
                        {part.description}
                      </td>
                      <td className="p-4">
                        <SparePartStatusBadge status={part.status} />
                      </td>
                      <td className="p-4 text-nb-black">
                        {(part as SparePartWithOrder).orders?.release_code || '—'}
                      </td>
                      <td className="p-4 text-nb-text-muted">
                        {new Date(part.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardNB>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-nb-text-muted">
                Page {currentPage + 1} of {totalPages} ({totalCount} parts)
              </p>
              <div className="flex gap-2">
                <ButtonNB
                  variant="secondary"
                  size="sm"
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                >
                  <IconNB name="chevronLeft" size={16} />
                  Previous
                </ButtonNB>
                <ButtonNB
                  variant="secondary"
                  size="sm"
                  disabled={currentPage >= totalPages - 1}
                  onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                >
                  Next
                  <IconNB name="chevronRight" size={16} />
                </ButtonNB>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Modal */}
      <CreateSparePartModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        orderId=""
        onCreated={() => {
          setShowCreateModal(false);
          refetch();
        }}
      />
    </>
  );
}
